import { Period } from '../Types/Period';
import { getApiInstance } from './Api';

export interface ReadingStatsResponse {
  averageWordsPerMinuteRead: number;
  timeSpentReading: number;
  timeSpentOnChapterBuilder: number;
  timeSpentOnQuiz: number;
  timeSpentOnReport: number;
}

export interface QuizStatsResponse {
  accuracy: number;
}

export interface ChallengingWordsStatsResponse {
  totalChallengingWords: number;
}

export interface UserStats {
  averageWordsPerMinuteRead: number;
  accuracy: number;
  totalArticleTime: number;

  timeSpentReading: number;
  timeSpentOnChapterBuilder: number;
  timeSpentOnQuiz: number;
  timeSpentOnReport: number;
  challengingWords: number;
}

export class UserStatsApi {
  private async getReadingStats(
    period: Period | null,
  ): Promise<ReadingStatsResponse> {
    try {
      const response = await getApiInstance().get<ReadingStatsResponse>(
        `/user-stats/reading${period ? `?period=${period}` : ''}`,
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching reading stats:', error);
      throw error;
    }
  }

  private async getQuizStats(
    period: Period | null,
  ): Promise<QuizStatsResponse> {
    try {
      const response = await getApiInstance().get<QuizStatsResponse>(
        `/user-stats/quiz${period ? `?period=${period}` : ''}`,
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching quiz stats:', error);
      throw error;
    }
  }

  private async getChallengingWordsStats(
    period: Period | null,
  ): Promise<ChallengingWordsStatsResponse> {
    try {
      const response =
        await getApiInstance().get<ChallengingWordsStatsResponse>(
          `/user-stats/challenging-words${period ? `?period=${period}` : ''}`,
        );
      return response.data;
    } catch (error) {
      console.error('Error fetching challenging words stats:', error);
      throw error;
    }
  }

  private mapUserStats(
    readingStats: ReadingStatsResponse,
    quizStats: QuizStatsResponse,
    challengingWordsStats: ChallengingWordsStatsResponse,
  ): UserStats {
    return {
      averageWordsPerMinuteRead: readingStats.averageWordsPerMinuteRead,
      accuracy: quizStats.accuracy,
      timeSpentReading: readingStats.timeSpentReading,
      timeSpentOnChapterBuilder: readingStats.timeSpentOnChapterBuilder,
      timeSpentOnQuiz: readingStats.timeSpentOnQuiz,
      timeSpentOnReport: readingStats.timeSpentOnReport,
      totalArticleTime:
        readingStats.timeSpentReading +
        readingStats.timeSpentOnChapterBuilder +
        readingStats.timeSpentOnQuiz +
        readingStats.timeSpentOnReport,
      challengingWords: challengingWordsStats.totalChallengingWords,
    };
  }

  private async getUserStatsFromPeriod(
    period: Period | null,
  ): Promise<UserStats> {
    const [readingStats, quizStats, challengingWordsStats] = await Promise.all([
      this.getReadingStats(period),
      this.getQuizStats(period),
      this.getChallengingWordsStats(period),
    ]);

    return this.mapUserStats(readingStats, quizStats, challengingWordsStats);
  }

  public async getUserStats(
    currentPeriod: Period | null,
    pastPeriod?: Period | null,
  ): Promise<{ current: UserStats; previous?: UserStats }> {
    try {
      const current = await this.getUserStatsFromPeriod(currentPeriod);
      const previous = pastPeriod
        ? await this.getUserStatsFromPeriod(pastPeriod)
        : undefined;

      return { current, previous };
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  }
}
